/**
 * Transversal data filtering — applied once, before any chart builds traces.
 *
 * Filtering is distinct from grouping: it only subsets the visible rows. An
 * empty/absent filter list is a no-op, so a chart with no `filters` behaves
 * exactly as today.
 */

import type { DataPoint } from '../../types/dashboard';
import type { FilterSpec } from '../model';
import { toFiniteNumber } from './number';

function matches(row: DataPoint, filter: FilterSpec): boolean {
  if (filter.kind === 'categorical') {
    // No values listed → nothing to narrow, keep the row.
    if (!filter.values || filter.values.length === 0) return true;
    return filter.values.includes(String(row[filter.field]));
  }

  // numeric
  const { min, max } = filter;
  // No bounds → keep the row.
  if (min === undefined && max === undefined) return true;
  // A missing/blank/non-numeric cell is not a number, so it cannot satisfy a
  // numeric bound — exclude it rather than letting Number('')→0 sneak through.
  const value = toFiniteNumber(row[filter.field]);
  if (value === null) return false;
  if (min !== undefined && value < min) return false;
  if (max !== undefined && value > max) return false;
  return true;
}

/** Return the subset of `data` that satisfies every filter (logical AND). */
export function applyFilters(data: DataPoint[], filters?: FilterSpec[]): DataPoint[] {
  if (!filters || filters.length === 0) return data;
  return data.filter((row) => filters.every((filter) => matches(row, filter)));
}
